import { useMemo, useRef } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { renderIssue, issuesOf } from 'helpers/serverIssues';

// Server-side zod issues as inline field errors (phase 4,
// docs/features/server-zod-i18n/phase-4-inline-field-errors.md).
//
// Why a RESOLVER WRAPPER and not `methods.setError` (D15): the zod resolver
// replaces the entire errors object on every re-validation pass, so an
// external setError is blown away on the next keystroke. Baking the server
// issue into the resolver's own output makes it a normal validation error —
// it sticks through re-validation and clears the moment the user edits the
// field. Precedent: modules/Diagrams/hooks/useCrossEdgeResolver.js.
//
// Usage (composes with useCgForm's `resolver` param, or <Form>'s built-in
// seam — see Form.jsx):
//
//   const seam = useServerIssues({ schema, map: SERVER_FIELD_MAP });
//   const methods = useCgForm({ schema, resolver: seam.resolver });
//   ...
//   catch (err) {
//     const unmatched = seam.setServerIssues(err, methods);
//     if (unmatched.length > 0) notifyError(err);   // D17f: never swallow
//   }

const getAtPath = (obj, path) =>
  path.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), obj);

/** Default field targeting: the last path segment (matches the common
 *  flat-form-vs-nested-payload case, e.g. ['address','city'] → 'city'). */
const defaultTarget = (issue) =>
  Array.isArray(issue.path) && issue.path.length > 0 ? String(issue.path.at(-1)) : null;

export function useServerIssues({ schema, resolver: baseResolver, map } = {}) {
  // [{ field, issue, valueAtSet }] — mutated in place; the resolver reads it.
  const activeRef = useRef([]);
  const mapRef = useRef(map);
  mapRef.current = map;

  const base = useMemo(
    () => baseResolver ?? (schema ? zodResolver(schema) : undefined),
    [baseResolver, schema],
  );

  const resolver = useMemo(() => {
    return async (values, ctx, options) => {
      const baseResult = base
        ? await Promise.resolve(base(values, ctx, options))
        : { values, errors: {} };
      if (activeRef.current.length === 0) return baseResult;

      // Clear-on-edit: a server verdict can't be re-derived client-side, so
      // it stands until the user changes the offending field.
      activeRef.current = activeRef.current.filter(
        (e) => getAtPath(values, e.field) === e.valueAtSet,
      );
      if (activeRef.current.length === 0) return baseResult;

      const errors = { ...baseResult.errors };
      for (const entry of activeRef.current) {
        // Client-side zod copy wins on the same field — it is more actionable
        // when the value is fundamentally broken (same rule as cross-edge).
        if (errors[entry.field]) continue;
        errors[entry.field] = { type: 'server', message: renderIssue(entry.issue) };
      }
      return { values: baseResult.values, errors };
    };
  }, [base]);

  /**
   * Map an axios error's issues[] onto form fields and trigger a
   * re-validation so they render immediately.
   *
   * @returns the issues that matched NO known field — the caller must surface
   *   those some other way (banner/toast); a server error is never swallowed.
   */
  const setServerIssues = (error, methods) => {
    const issues = issuesOf(error);
    if (issues.length === 0) return issues;
    const values = methods.getValues();
    const unmatched = [];
    const matched = [];
    for (const issue of issues) {
      const field = mapRef.current?.(issue, values) ?? defaultTarget(issue);
      // "Known field" heuristic: the path exists in the form's value tree
      // (defaultValues make every registered field present).
      if (field && getAtPath(values, field) !== undefined) {
        matched.push({ field, issue, valueAtSet: getAtPath(values, field) });
      } else {
        unmatched.push(issue);
      }
    }
    if (matched.length > 0) {
      activeRef.current = matched;
      methods.trigger(matched.map((m) => m.field));
    }
    return unmatched;
  };

  const clearServerIssues = () => {
    activeRef.current = [];
  };

  return { resolver, setServerIssues, clearServerIssues };
}

export default useServerIssues;
